import React, { useState, useEffect } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import Home from '../src/components/Home';
import ModeSelector from '../src/components/ModeSelector';
import FlightMode from '../src/components/FlightMode';
import './index';

// ─────────────────────────────────────────────
// App — root component managing mode selection
// Modes: 'select' | 'simple' | 'flight'
// ─────────────────────────────────────────────

function App() {
  const [mode, setMode] = useState('select'); // 'select' | 'simple' | 'flight'
  const [targetSection, setTargetSection] = useState(null);

  const [isDarkMode, setIsDarkMode] = useState(() => {
    if (typeof window !== 'undefined') {
      return (
        localStorage.getItem('theme') === 'dark' ||
        (!localStorage.getItem('theme') &&
          window.matchMedia('(prefers-color-scheme: dark)').matches)
      );
    }
    return true;
  });

  useEffect(() => {
    document.body.setAttribute('data-theme', isDarkMode ? 'dark' : 'light');
  }, [isDarkMode]);

  const handleModeSelect = (selectedMode) => {
    setMode(selectedMode);
  };

  const handleExitFlight = () => {
    // Return to simple portfolio
    setMode('simple');
  };

  const handleNavigateToSection = (section) => {
    setTargetSection(section);
    setMode('simple');
  };

  // After simple mode loads, scroll to target section
  useEffect(() => {
    if (mode === 'simple' && targetSection) {
      const t = setTimeout(() => {
        const el = document.getElementById(targetSection);
        if (el) el.scrollIntoView({ behavior: 'smooth' });
        setTargetSection(null);
      }, 400);
      return () => clearTimeout(t);
    }
  }, [mode, targetSection]);

  return (
    <div className="app" data-theme={isDarkMode ? 'dark' : 'light'}>
      <AnimatePresence mode="wait">
        {mode === 'select' && (
          <motion.div
            key="select"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.45 }}
          >
            <ModeSelector onSelect={handleModeSelect} />
          </motion.div>
        )}

        {mode === 'simple' && (
          <motion.div
            key="simple"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.45 }}
          >
            {/* Flight Mode launch button — floats in corner */}
            <motion.button
              className="flight-mode-fab"
              onClick={() => setMode('flight')}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 1.2, duration: 0.5 }}
              whileHover={{ scale: 1.06, y: -3 }}
              whileTap={{ scale: 0.95 }}
              title="Switch to Flight Mode"
              aria-label="Switch to Flight Mode"
            >
              <FabPlaneIcon />
              <span>Flight Mode</span>
            </motion.button>

            <Home
              isDarkMode={isDarkMode}
              setIsDarkMode={setIsDarkMode}
            />
          </motion.div>
        )}

        {mode === 'flight' && (
          <motion.div
            key="flight"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.5 }}
            style={{ position: 'fixed', inset: 0, zIndex: 9000 }}
          >
            <FlightMode
              onExit={handleExitFlight}
              onNavigateToSection={handleNavigateToSection}
            />
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

const FabPlaneIcon = () => (
  <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
    <path d="M1 9L9 2L17 9M9 2.5L15 10H11.5L13 16H5L6.5 10H3L9 2.5Z" fill="currentColor" stroke="currentColor" strokeWidth="0.3" strokeLinecap="round"/>
  </svg>
);

export default App;
